/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/Profile/ExpertProfilePage.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import "./ExpertProfile.css";
import ChangePasswordModal from "../../../../components/common/ChangePasswordModal";
import ChangeEmailModal from "../../../../components/common/ChangeEmailModal";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";

interface ExpertProfileData {
  id: string;
  name: string;
  email: string;
  role: string;
  userCode?: string;
  createdAt: string;
  material?: {
    id: number;
    name: string;
  };
}

const ExpertProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<ExpertProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        console.log("📡 Fetching expert profile...");
        const res = await axiosClient.get("/profile/me");
        console.log("✅ Profile loaded:", res.data);
        setProfile(res.data);
      } catch (err: any) {
        console.error("❌ Profile fetch error:", err);
        setError(err.response?.data?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="My Profile" />
        <div className="profile-loading-state">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="My Profile" />
        <div className="profile-error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="My Profile" />
        <div className="profile-empty-state">
          <div className="empty-icon">👤</div>
          <h3>No Profile Data</h3>
          <p>Profile information is not available.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="background-form-container analysis-shell">
      <AnalysisBanner title="My Profile" />
      <div className="expert-profile-container">

      {/* Profile Card */}
      <div className="profile-card">
        {/* Avatar Section */}
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            <img
              src={`https://api.dicebear.com/8.x/initials/svg?seed=${profile.name}`}
              alt={profile.name}
              className="avatar-image"
            />
            <div className="avatar-badge">
              <span className="badge-icon">✓</span>
            </div>
          </div>
          <div className="profile-name-section">
            <h2 className="profile-name">{profile.name || "Expert User"}</h2>
            <div className="profile-role-badge">
              <span>{profile.role || "EXPERT"}</span>
            </div>
          </div>
        </div>

        {/* Profile Information Grid */}
        <div className="profile-info-grid">
          <div className="info-card">
            <div className="info-icon email-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="info-content">
              <label className="info-label">Email address</label>
              <p className="info-value">{profile.email || "Not provided"}</p>
            </div>
          </div>

          <div className="info-card">
            <div className="info-icon date-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.8333 3.33333H4.16667C3.24619 3.33333 2.5 4.07952 2.5 5V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V5C17.5 4.07952 16.7538 3.33333 15.8333 3.33333Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.3333 1.66667V5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66667 1.66667V5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.5 8.33333H17.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="info-content">
              <label className="info-label">Member since</label>
              <p className="info-value">
                {profile.createdAt ? formatDate(profile.createdAt) : "N/A"}
              </p>
            </div>
          </div>

          <div className="info-card">
            <div className="info-icon id-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 11.6667C12.3012 11.6667 14.1667 9.80118 14.1667 7.5C14.1667 5.19882 12.3012 3.33333 10 3.33333C7.69882 3.33333 5.83333 5.19882 5.83333 7.5C5.83333 9.80118 7.69882 11.6667 10 11.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 11.6667C6.3181 11.6667 3.33333 13.3179 3.33333 15.4167V18.3333H16.6667V15.4167C16.6667 13.3179 13.6819 11.6667 10 11.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="info-content">
              <label className="info-label">User ID</label>
              <p className="info-value id-value">{profile.userCode || profile.id || "N/A"}</p>
            </div>
          </div>

          {profile.material && (
            <div className="info-card">
              <div className="info-icon material-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10 2.5L3.33333 6.25V10C3.33333 13.6819 5.98477 16.3333 9.66667 16.6667M10 2.5L16.6667 6.25V10C16.6667 13.6819 14.0152 16.3333 10.3333 16.6667M10 2.5V16.6667M3.33333 6.25L10 10L16.6667 6.25M10 10V16.6667"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="info-content">
                <label className="info-label">Assigned Material</label>
                <p className="info-value">{profile.material.name || "Not assigned"}</p>
              </div>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="profile-security-section">
          <div className="security-header">
            <h3 className="security-title">Security</h3>
            <p className="security-subtitle">Manage your account security settings</p>
          </div>
          <div className="security-actions">
            <button
              className="change-password-btn"
              onClick={() => setShowChangePasswordModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 1.66667V3.33333M10 16.6667V18.3333M18.3333 10H16.6667M3.33333 10H1.66667M15.7733 4.22667L14.6067 5.39333M5.39333 14.6067L4.22667 15.7733M15.7733 15.7733L14.6067 14.6067M5.39333 5.39333L4.22667 4.22667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Change Password
            </button>
            <button
              className="change-email-btn"
              onClick={() => setShowChangeEmailModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Change Email
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={() => {
          // Optionally show a success message or refresh profile
        }}
      />

      {/* Change Email Modal */}
      <ChangeEmailModal
        open={showChangeEmailModal}
        onClose={() => setShowChangeEmailModal(false)}
        currentEmail={profile.email}
        onSuccess={() => {
          // Refresh profile to get updated email
          window.location.reload();
        }}
      />
    </div>
  );
};

/** React component — Expert Profile Page. */
export default ExpertProfilePage;
